/**
 * Content-aware ordering for the Gallery page photo grid.
 *
 * Photo gallery items only carry a coarse `title` ("Apartment" / "Community"),
 * so each photo is bucketed by keywords in its alt text and description, then
 * ordered: property exterior → outdoor community → indoor community →
 * apartment interiors (kitchen → island → living → dining → bedroom →
 * bathroom → closet → laundry → patio → other). Within a bucket, staged and
 * high-resolution shots lead; weak shots (toilets, empty or dirty spaces,
 * low-res) sink to the very end of the list so they never sit up front.
 */

export type GalleryGroup = 'community' | 'apartment';

interface Bucket {
  id: string;
  group: GalleryGroup;
  /** Display order across the whole gallery (lower = earlier). */
  rank: number;
  pattern: RegExp;
  /** Specific features win over a generic room match (kitchen island → island). */
  specific?: boolean;
  /** Catch-all: only used when no other bucket in the group matches. */
  fallback?: boolean;
}

const b = (words: string) => new RegExp(`\\b(?:${words})\\b`, 'i');

// Rank order follows the requested gallery sequence.
const BUCKETS: Bucket[] = [
  // Property exterior
  { id: 'exterior', group: 'community', rank: 10, pattern: b('exteriors?|buildings?|fa[cç]ade|aerial|architecture|architectural|elevation|street ?view|curb appeal|property view') },
  { id: 'signage', group: 'community', rank: 11, pattern: b('signs?|signage|monument|logo|marquee') },
  { id: 'entrance', group: 'community', rank: 12, pattern: b('entrances?|entry ?way|entryway|main entry|front entry|gated? entry|gates?|porte[- ]coch[eè]re|drive ?way|arrival') },

  // Outdoor community
  { id: 'pool', group: 'community', rank: 20, pattern: b('pools?|poolside|swimming|swim|cabanas?|sun ?deck|lounge chairs?|hot tub|jacuzzi|splash pad') },
  { id: 'grill', group: 'community', rank: 21, pattern: b('grills?|grilling|bbq|barbecue|fire ?pits?|outdoor kitchen|picnic|pavilion|outdoor dining|outdoor entertain\\w*') },
  { id: 'playground', group: 'community', rank: 22, pattern: b('playgrounds?|play ?area|tot lot|play structure'), specific: true },
  { id: 'recreation', group: 'community', rank: 23, pattern: b('courtyards?|lawns?|gardens?|green ?space|trails?|walking path|hammocks?|bocce|cornhole|rooftop|roof deck|sky ?deck|outdoor lounge|recreation\\w*|park') },
  { id: 'sports', group: 'community', rank: 24, pattern: b('tennis|basketball|pickleball|volleyball|sports? courts?|putting green|golf|soccer'), specific: true },
  { id: 'dogpark', group: 'community', rank: 25, pattern: b('dog parks?|bark parks?|pet parks?|dog runs?|pet play\\w*|off[- ]leash|pet area'), specific: true },
  { id: 'outdoorOther', group: 'community', rank: 26, pattern: b('outdoor|outside|grounds|landscap\\w*|parking|garages?|carports?|bike|ev charg\\w*'), fallback: true },

  // Indoor community
  { id: 'clubhouse', group: 'community', rank: 30, pattern: b('clubhouse|club ?room|resident lounge|lounge|game room|billiards?|pool table|social room|community kitchen|demonstration kitchen') },
  { id: 'lobby', group: 'community', rank: 31, pattern: b('lobby|leasing( office| center)?|reception|concierge|front desk') },
  { id: 'business', group: 'community', rank: 32, pattern: b('business cent(er|re)|co-?working|conference|work ?spaces?|work from home|study|library|private offices?|meeting room'), specific: true },
  { id: 'theater', group: 'community', rank: 33, pattern: b('theat(er|re)|media room|cinema|screening'), specific: true },
  { id: 'fitness', group: 'community', rank: 34, pattern: b('fitness|gym|yoga|workout|exercise|weights?|cardio|treadmills?|spin'), specific: true },
  { id: 'spa', group: 'community', rank: 35, pattern: b('spa|sauna|steam room|massage|wellness'), specific: true },
  { id: 'package', group: 'community', rank: 36, pattern: b('packages?|parcels?|lockers?|mail ?room|mailbox(es)?|amazon hub'), specific: true },
  { id: 'petwash', group: 'community', rank: 37, pattern: b('pet wash\\w*|dog wash\\w*|pet spa|grooming|pet grooming'), specific: true },
  { id: 'indoorOther', group: 'community', rank: 38, pattern: b('amenit(y|ies)|community room|hallways?|corridors?|elevators?|indoor|common area|storage'), fallback: true },

  // Apartment interiors
  { id: 'kitchen', group: 'apartment', rank: 50, pattern: b('kitchens?|cabinets?|cabinetry|counter ?tops?|countertops?|quartz|granite|backsplash|appliances?|refrigerator|fridge|range|oven|dishwasher|microwave|pantry') },
  { id: 'island', group: 'apartment', rank: 51, pattern: b('islands?|peninsulas?|breakfast bar|bar seating'), specific: true },
  { id: 'living', group: 'apartment', rank: 52, pattern: b('living( room| area| space)?|family room|great room|fireplace|sofa|couch') },
  { id: 'dining', group: 'apartment', rank: 53, pattern: b('dining|dinette|eat-in|breakfast nook') },
  { id: 'bedroom', group: 'apartment', rank: 54, pattern: b('bedrooms?|bed|primary suite|master suite|guest room') },
  { id: 'bathroom', group: 'apartment', rank: 55, pattern: b('bath(room)?s?|showers?|tub|bathtub|vanity|vanities|toilet|powder room|en-?suite') },
  { id: 'closet', group: 'apartment', rank: 56, pattern: b('closets?|walk-?in|wardrobe'), specific: true },
  { id: 'laundry', group: 'apartment', rank: 57, pattern: b('washers?|dryers?|laundry|w/d|washer/dryer'), specific: true },
  { id: 'patio', group: 'apartment', rank: 58, pattern: b('patios?|balcon(y|ies)|private deck|porch|terrace|sunroom|veranda') },
  { id: 'apartmentOther', group: 'apartment', rank: 59, pattern: b('apartments?|interiors?|units?|home|flooring|floors|windows?|den|office|foyer|entry|hallway|ceilings?|lighting|smart home|thermostat'), fallback: true },
];

const GROUP_FALLBACK: Record<GalleryGroup, number> = {
  community: 39,
  apartment: 59,
};

const STAGED = b('staged|furnished|model|decorated|designer');
const UNSTAGED = b('vacant|unfurnished|unstaged|empty');
const WEAK = b('toilets?|commode|dirty|messy|cluttered|blurry|poorly lit|under construction|damaged|stained|empty closets?|empty (?:patio|balcony)');

/** Score at or below this sinks to the end of the whole list. */
const WEAK_THRESHOLD = -3;

export interface SortableGalleryImage {
  image?: string;
  original_url?: string;
  alt_text?: string;
  title?: string;
  description?: string;
  image_media?: { alt_text?: string | null; width?: number | null; height?: number | null } | null;
}

interface Classified<T> {
  item: T;
  index: number;
  group: GalleryGroup;
  rank: number;
  score: number;
}

function imageText(img: SortableGalleryImage): string {
  return [img.alt_text, img.image_media?.alt_text, img.description]
    .filter(Boolean)
    .join(' · ');
}

/**
 * A specific feature beats a generic room; otherwise the earliest-mentioned
 * bucket wins, and the longer phrase wins a tie ("pool table" over "pool").
 */
function matchBucket(text: string, group: GalleryGroup): Bucket | null {
  let best: { bucket: Bucket; at: number; len: number } | null = null;
  for (const bucket of BUCKETS) {
    if (bucket.group !== group || bucket.fallback) continue;
    const m = bucket.pattern.exec(text);
    if (!m) continue;
    const candidate = { bucket, at: m.index, len: m[0].length };
    if (!best) {
      best = candidate;
      continue;
    }
    const specificWins = !!bucket.specific && !best.bucket.specific;
    const specificLoses = !bucket.specific && !!best.bucket.specific;
    const earlier =
      candidate.at < best.at || (candidate.at === best.at && candidate.len > best.len);
    if (specificWins || (!specificLoses && earlier)) best = candidate;
  }
  if (best) return best.bucket;
  return BUCKETS.find((x) => x.group === group && x.fallback && x.pattern.test(text)) ?? null;
}

function qualityScore(img: SortableGalleryImage, text: string): number {
  let score = 0;
  if (STAGED.test(text)) score += 2;
  if (UNSTAGED.test(text)) score -= 2;
  if (WEAK.test(text)) score -= 3;
  const width = img.image_media?.width ?? 0;
  const height = img.image_media?.height ?? 0;
  const longEdge = Math.max(width, height);
  if (longEdge > 0 && longEdge < 800) score -= 3;
  else if (longEdge >= 1600) score += 1;
  return score;
}

function imageKey(img: SortableGalleryImage): string {
  return (img.image || img.original_url || '').split('?')[0];
}

/**
 * Classify photos and split them into community / apartment lists, each
 * sorted per the gallery rules. `group` is the photo's declared tab (from
 * its title); a photo is only moved to the other tab when nothing in its
 * declared group matches but the other group clearly does (e.g. an
 * "Apartment"-titled shot of the fitness center). Pass
 * `allowCrossGroup: false` to keep every photo in its declared tab.
 */
export function sortGalleryImages<T extends SortableGalleryImage>(
  photos: { item: T; group: GalleryGroup }[],
  { allowCrossGroup = true }: { allowCrossGroup?: boolean } = {},
): { community: T[]; apartment: T[] } {
  const seen = new Set<string>();
  const classified: Classified<T>[] = [];

  photos.forEach(({ item, group }, index) => {
    const key = imageKey(item);
    if (!key || seen.has(key)) return;
    seen.add(key);

    const text = imageText(item);
    const other: GalleryGroup = group === 'community' ? 'apartment' : 'community';
    const own = matchBucket(text, group);
    const cross = own || !allowCrossGroup ? null : matchBucket(text, other);
    const bucket = own ?? cross;
    const finalGroup = bucket?.group ?? group;

    classified.push({
      item,
      index,
      group: finalGroup,
      rank: bucket?.rank ?? GROUP_FALLBACK[finalGroup],
      score: qualityScore(item, text),
    });
  });

  const order = (list: Classified<T>[]) => {
    const strong = list.filter((c) => c.score > WEAK_THRESHOLD);
    const weak = list.filter((c) => c.score <= WEAK_THRESHOLD);
    const byBucket = (a: Classified<T>, c: Classified<T>) =>
      a.rank - c.rank || c.score - a.score || a.index - c.index;
    return [...strong.sort(byBucket), ...weak.sort(byBucket)].map((c) => c.item);
  };

  return {
    community: order(classified.filter((c) => c.group === 'community')),
    apartment: order(classified.filter((c) => c.group === 'apartment')),
  };
}
